/**
 * src/utils/escapeStack.ts
 *
 * Who Escape belongs to, when several things could answer it.
 *
 * Two surfaces both wanted the key and both took it. `SpatialObjectChatCard`
 * closes the conversation on Escape; `useAgentTeleportMode` exits cinematic
 * on Escape. Each added its own `window` keydown listener, so with a chat
 * open during a cinematic, one keypress ran both — the chat closed and the
 * camera fell out of cinematic at the same time. A visitor who wanted to
 * stop talking lost the view they were talking about.
 *
 * WHY NOT STOP THE EVENT. The quick fix is for the chat to listen in the
 * capture phase and call `stopImmediatePropagation`, which does end the
 * double-handling. It ends it by breaking Escape for everything else on the
 * page, including listeners neither surface knows about, and it makes the
 * outcome depend on listener registration order — which in React is
 * effect order, which changes when somebody reorders hooks. A fix that
 * silently depends on hook order is a bug waiting for an unrelated commit.
 *
 * WHAT THIS DOES INSTEAD. Ownership is explicit. Every dismissible thing
 * registers itself with a declared priority. One listener — installed by
 * this module, once — reads the registry and calls exactly one owner: the
 * highest priority, and among equals the most recently registered, which is
 * the one the visitor interacted with last.
 *
 * Nothing here calls `preventDefault`, `stopPropagation` or
 * `stopImmediatePropagation`. The event continues on its way and unrelated
 * global handlers still see it. This module decides which of ITS OWN
 * registrants runs, and claims no authority over anybody else's.
 *
 * The priorities are the whole design. A transient surface outranks an
 * ambient mode, so Escape closes the thing you just opened before it
 * unwinds the mode you have been in — and a second Escape unwinds the mode,
 * because by then the surface has deregistered. That is the order a person
 * expects, and it is deterministic rather than emergent.
 */

import { useEffect } from 'react';

/**
 * How strongly something claims Escape.
 *
 * Named rather than numeric at the call sites, because `priority: 100` in a
 * component tells the next reader nothing about why it beats `10`.
 */
export const ESCAPE_PRIORITY = Object.freeze({
  /**
   * A thing opened on purpose and closed the same way: a conversation, a
   * modal, a picker. Outranks a mode because it is newer and smaller — the
   * visitor is dismissing what they just opened, not leaving where they are.
   */
  SURFACE: 100,
  /**
   * A mode the visitor is in rather than a thing they opened: cinematic,
   * teleport, a full-screen camera state. Dismissed only once nothing is
   * layered on top of it.
   */
  MODE: 10,
} as const);

export interface EscapeOwner {
  /** For diagnostics and for tests to assert on. Not required to be unique. */
  id: string;
  priority: number;
  onEscape: () => void;
}

/**
 * The registry, in registration order.
 *
 * Module-level rather than a React context on purpose: `useAgentTeleportMode`
 * is a renderless hook called from `App`, and the chat card is mounted deep
 * inside the map layer. A context spanning both would have to wrap most of
 * the application to solve a problem about one key.
 */
const _owners: Array<EscapeOwner & { seq: number }> = [];

let _seq = 0;
let _listening = false;

/**
 * The owner Escape would go to right now.
 *
 * Highest priority wins; ties go to whoever registered last. Exported so
 * tests can assert the ordering directly rather than inferring it from
 * which callback happened to fire.
 */
export function topEscapeOwner(): EscapeOwner | null {
  let best: (EscapeOwner & { seq: number }) | null = null;
  for (const owner of _owners) {
    if (!best) { best = owner; continue; }
    if (owner.priority > best.priority) { best = owner; continue; }
    // Equal priority: the newer registration is the one in front.
    if (owner.priority === best.priority && owner.seq > best.seq) best = owner;
  }
  return best ? { id: best.id, priority: best.priority, onEscape: best.onEscape } : null;
}

/**
 * Dispatch one Escape.
 *
 * Exported for tests, which would otherwise have to synthesise a
 * `KeyboardEvent` to check the ordering rules.
 */
export function dispatchEscape(): boolean {
  const owner = topEscapeOwner();
  if (!owner) return false;
  owner.onEscape();
  return true;
}

function _onKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  // A visitor pressing Escape inside a text field is generally not asking
  // to close the surface around it, but our own composer has no other
  // meaning for the key, so this deliberately does not special-case
  // inputs. If that changes, it changes here rather than in each owner.
  //
  // Nothing is consumed: no preventDefault, no stopPropagation. Handlers
  // outside this registry are unaffected by design.
  dispatchEscape();
}

function _ensureListening(): void {
  if (_listening || typeof window === 'undefined') return;
  window.addEventListener('keydown', _onKeyDown);
  _listening = true;
}

/**
 * Claim Escape while something is open, and release it afterwards.
 *
 * Returns the release function, so a caller outside React can use this
 * directly. The listener is installed on first registration and left
 * installed — one idle keydown handler is cheaper than adding and removing
 * one every time a tooltip opens.
 */
export function pushEscapeOwner(owner: EscapeOwner): () => void {
  _ensureListening();
  _seq += 1;
  const entry = { ...owner, seq: _seq };
  _owners.push(entry);

  let released = false;
  return () => {
    // Guarded because React may call a cleanup twice in development, and
    // the second call must not remove a later registration that happens to
    // share this object's identity.
    if (released) return;
    released = true;
    const at = _owners.indexOf(entry);
    if (at >= 0) _owners.splice(at, 1);
  };
}

/**
 * Claim Escape for as long as `active` holds.
 *
 * The `active` flag is a parameter rather than something the caller wraps
 * this hook in, because hooks cannot be called conditionally and the
 * alternative is every caller writing the same early-return effect.
 *
 * `onEscape` is read through a ref-free dependency on purpose: it is in the
 * dependency list, so a caller passing an unstable callback re-registers.
 * That is correct — it moves them to the front of their priority band,
 * which for a surface being interacted with is what should happen.
 */
export function useEscapeOwner(
  active: boolean,
  id: string,
  priority: number,
  onEscape: () => void,
): void {
  useEffect(() => {
    if (!active) return undefined;
    return pushEscapeOwner({ id, priority, onEscape });
  }, [active, id, priority, onEscape]);
}

/**
 * Drop every registration. Tests only.
 *
 * Module state outlives a test file's individual cases, and an owner left
 * behind by one case would silently take Escape in the next.
 */
export function __resetEscapeOwnersForTests(): void {
  _owners.length = 0;
  _seq = 0;
}
